import React, { useEffect, useRef, useState } from 'react';
import { useTheme } from '../context/ThemeContext'; // 👈 Theme Support Added

const WalkingCursor = () => {
  const { isDark } = useTheme(); // 👈 Access Theme State
  const cursorRef = useRef(null);
  const trailContainerRef = useRef(null);
  const [isWalking, setIsWalking] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const walkingTimeoutRef = useRef(null);

  // Tracks the last position a trail dot was dropped
  const lastTrailPos = useRef({ x: 0, y: 0 });

  useEffect(() => {
    // Check if the device is mobile/touchscreen (Coarse pointer means touch device)
    const mediaQuery = window.matchMedia('(pointer: coarse)');
    if (mediaQuery.matches) {
      setIsMobile(true);
      return; // Do not apply custom cursor logic on mobile devices
    }

    // Hide the default cursor everywhere for desktop
    document.body.style.cursor = 'none';
    
    const style = document.createElement('style');
    style.innerHTML = `
      * { cursor: none !important; }
      a, button, input, select, textarea { cursor: none !important; }
    `;
    document.head.appendChild(style);

    const handleMouseMove = (e) => {
      const { clientX: x, clientY: y } = e;

      // 1. Move the Main Stick Figure Cursor
      if (cursorRef.current) {
        cursorRef.current.style.transform = `translate3d(${x - 10}px, ${y - 10}px, 0)`;
      }

      // 2. Trigger the "Walking" Animation
      setIsWalking(true);
      clearTimeout(walkingTimeoutRef.current);
      walkingTimeoutRef.current = setTimeout(() => {
        setIsWalking(false); 
      }, 100);

      // 3. Generate the Fading Trail
      const dist = Math.hypot(x - lastTrailPos.current.x, y - lastTrailPos.current.y);
      
      if (dist > 15 && trailContainerRef.current) {
        lastTrailPos.current = { x, y };

        const dot = document.createElement('div');
        dot.className = 'cursor-trail-dot';
        dot.style.left = `${x}px`;
        dot.style.top = `${y}px`;
        trailContainerRef.current.appendChild(dot);

        setTimeout(() => {
          dot.style.opacity = '0';
          dot.style.transform = 'translate(-50%, -50%) scale(0.2)';
        }, 10); 

        setTimeout(() => {
          if (trailContainerRef.current?.contains(dot)) {
            trailContainerRef.current.removeChild(dot);
          }
        }, 600); 
      }
    };

    window.addEventListener('mousemove', handleMouseMove);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      document.body.style.cursor = 'auto';
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
      clearTimeout(walkingTimeoutRef.current);
    };
  }, []);

  // If viewed on mobile/touch device, render nothing so standard touch functionality is unhindered
  if (isMobile) return null;

  return (
    <>
      <div 
        ref={trailContainerRef} 
        style={{ 
          pointerEvents: 'none', 
          zIndex: 999998, 
          position: 'fixed', 
          top: 0, 
          left: 0, 
          width: '100%', 
          height: '100%' 
        }} 
      />
      
      <div 
        ref={cursorRef} 
        style={{ 
          position: 'fixed', 
          top: 0, 
          left: 0, 
          pointerEvents: 'none', 
          zIndex: 999999,
          willChange: 'transform' 
        }}
      >
        {/* The SVG Stick Figure - Stroke color dynamically shifts for dark mode */}
        <svg 
          viewBox="0 0 24 24" 
          width="28" 
          height="28" 
          stroke={isDark ? '#f9fafb' : '#111827'} 
          strokeWidth="2" 
          fill="none" 
          strokeLinecap="round" 
          strokeLinejoin="round" 
          className={isWalking ? 'walking-animation' : ''}
          style={{ transition: 'stroke 0.3s ease' }}
        >
          {/* Head */}
          <circle cx="12" cy="5" r="2.5"></circle>
          {/* Body */}
          <line x1="12" y1="7.5" x2="12" y2="15"></line>
          {/* Arms */}
          <line x1="12" y1="10" x2="7" y2="13"></line>
          <line x1="12" y1="10" x2="17" y2="13"></line>
          {/* Legs */}
          <line x1="12" y1="15" x2="8" y2="22"></line>
          <line x1="12" y1="15" x2="16" y2="22"></line>
        </svg>
      </div>

      <style>{`
        .cursor-trail-dot {
          position: absolute;
          width: 8px;
          height: 8px;
          background-color: #16a34a; 
          border-radius: 50%;
          pointer-events: none;
          transform: translate(-50%, -50%) scale(1);
          opacity: 0.6;
          transition: opacity 0.6s ease-out, transform 0.6s ease-out;
        }

        .walking-animation {
          animation: walkWiggle 0.3s infinite alternate ease-in-out;
          transform-origin: center center;
        }

        @keyframes walkWiggle {
          0% { transform: rotate(-15deg) translateY(-2px); }
          100% { transform: rotate(15deg) translateY(2px); }
        }
      `}</style>
    </>
  );
};

export default WalkingCursor;